import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO, parseFecha, claveMes } from '../lib/format';
import { q, eliminarMovimiento, buscarVinculos } from '../lib/vinculos';
import { gastoFijoPagado } from '../lib/finanzas';
import { sumarDias } from '../lib/dia';
import { useAccion } from '../lib/useAccion';
import { ivaDesdeTotal } from '../lib/iva';
import { gastadoPorPresupuesto, estadoPresupuesto } from '../lib/presupuestos';
import {
  PORCENTAJES_COMPARTIDO, personaCompartidaGuardada, parteCompartida, validarCompartido, personasConocidas,
} from '../lib/compartido';
import { nombreRubro, ACEPTA_ARCHIVOS, abrirComprobante, comprobantesDeMovimientos, subirComprobante } from '../lib/comprobantes';
import { registrarMovimiento } from '../lib/registrar';
import { leerCaptura } from '../lib/captura';
import { SelectRubro } from './ComprobanteCampos';
import ErrorBanner from './ErrorBanner';

// La última categoría usada queda elegida para el próximo gasto (casi siempre se paga desde la misma).
const CLAVE_BOLSILLO = 'registroDiarioBolsillo';
const bolsilloGuardado = () => { try { return localStorage.getItem(CLAVE_BOLSILLO) || ''; } catch { return ''; } };
const guardarBolsillo = (id) => { try { localStorage.setItem(CLAVE_BOLSILLO, id); } catch { /* sin almacenamiento */ } };

const nombreDia = (fecha) => {
  const texto = parseFecha(fecha).toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const formVacio = (tipo, fecha, bolsilloId) => ({
  tipo, fecha, bolsilloId, monto: '', descripcion: '', rubro: '',
  vincular: 'gasto', gasto_fijo_id: '', deuda_id: '', presupuesto_id: '', conFactura: false,
  compartido: false, compartidoCon: personaCompartidaGuardada(), compartidoPct: 50,
  archivo: null,
});

// Gasto fijo que probablemente corresponde a lo leído (ej. "Enel — luz octubre" → gasto fijo "Enel").
function adivinarGastoFijo(descripcion, pendientes) {
  const d = (descripcion || '').toLowerCase();
  return pendientes.find(g => {
    const palabra = (g.nombre || '').toLowerCase().split(/\s+/).find(p => p.length >= 4);
    return palabra && d.includes(palabra);
  });
}

const Bloque = ({ titulo, texto, children }) => (
  <section style={{ marginBottom: 22 }}>
    <h3 style={{ fontSize: '1.05rem', margin: '0 0 4px' }}>{titulo}</h3>
    <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{texto}</p>
    {children}
  </section>
);

function RegistroDiario({ userId }) {
  const [bolsillos, setBolsillos] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [gastosF, setGastosF] = useState([]);
  const [deudas, setDeudas] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [comprobantes, setComprobantes] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fecha, setFecha] = useState(hoyISO());
  const [form, setForm] = useState(null);
  const [leyendo, setLeyendo] = useState(false);
  const [lectura, setLectura] = useState(null); // { ok, texto } lo que dijo la IA
  const [coincide, setCoincide] = useState(null); // { mov, file }: la captura es de algo ya registrado
  const { saving, error, setError, run: ejecutar } = useAccion();

  const loadData = useCallback(async () => {
    if (!userId) return;
    try {
      const [b, m, gf, deu, pres, comps, pers] = await Promise.all([
        q(supabase.from('bolsillos').select('*').eq('user_id', userId).order('created_at')),
        q(supabase.from('bolsillo_movimientos').select('*').eq('user_id', userId).order('fecha')),
        q(supabase.from('gastos_fijos').select('*').eq('user_id', userId).order('created_at')),
        q(supabase.from('deudas').select('*').eq('user_id', userId).eq('activa', true).order('created_at')),
        q(supabase.from('presupuestos').select('*').eq('user_id', userId).eq('activo', true).order('created_at')),
        comprobantesDeMovimientos(userId),
        personasConocidas(userId),
      ]);
      setBolsillos(b || []);
      setMovimientos(m || []);
      setGastosF(gf || []);
      setDeudas(deu || []);
      setPresupuestos(pres || []);
      setComprobantes(comps || []);
      setPersonas(pers || []);
    } catch (e) {
      setError('Error cargando datos: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [userId, setError]);

  useEffect(() => { loadData(); }, [loadData]);

  const run = (accion) => ejecutar(accion, loadData);

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  const hoy = hoyISO();
  const nombreBolsillo = (id) => bolsillos.find(b => b.id === id)?.nombre || 'Categoría eliminada';
  const delDia = movimientos.filter(m => m.fecha === fecha)
    .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
  const salidas = delDia.filter(m => m.tipo === 'egreso');
  const entradas = delDia.filter(m => m.tipo === 'ingreso');
  // Traspasos, ajustes y préstamos se listan, pero no suman a "Pagaste" ni a "Te entró"
  const total = (lista) => lista.filter(m => !m.clase).reduce((s, m) => s + Number(m.monto || 0), 0);
  const totalEmpresa = total(salidas.filter(m => m.rubro === 'empresa'));
  const comprobanteDe = (movId) => comprobantes.find(c => c.movimiento_id === movId);

  const abrirForm = (tipo) => {
    const guardado = bolsilloGuardado();
    const bolsilloId = bolsillos.some(b => b.id === guardado) ? guardado : (bolsillos[0]?.id || '');
    setForm(formVacio(tipo, fecha, bolsilloId));
    setLectura(null);
  };
  const cerrarForm = () => { setForm(null); setLectura(null); setCoincide(null); };

  // Movimiento ya registrado (ej. cargado desde la cartola) que calza con lo leído:
  // mismo tipo y monto, hasta 3 días de diferencia, y todavía sin comprobante.
  const buscarYaRegistrado = (tipo, monto, fechaLeida) => {
    if (!monto) return null;
    const ref = parseFecha(fechaLeida);
    return movimientos.find(m => m.tipo === tipo && Number(m.monto) === Number(monto) && !comprobanteDe(m.id)
      && Math.abs(parseFecha(m.fecha) - ref) <= 3 * 86400000) || null;
  };

  const adjuntarAExistente = async () => {
    const { mov, file } = coincide;
    const ok = await run(() => subirComprobante({
      userId, file, fecha: mov.fecha, monto: mov.monto, concepto: mov.descripcion || file.name,
      rubro: mov.rubro || null, gastoFijoId: mov.gasto_fijo_id || null, movimientoId: mov.id,
    }));
    if (ok) { setFecha(mov.fecha); cerrarForm(); }
  };
  const set = (cambios) => setForm(f => ({ ...f, ...cambios }));

  const pendientesGF = form ? gastosF.filter(g => !gastoFijoPagado(g, parseFecha(form.fecha) || new Date())) : [];

  // --- Leer la captura con IA y prellenar ---
  const handleCaptura = async (file) => {
    if (!file) return;
    set({ archivo: file });
    setLectura(null);
    setCoincide(null);
    setLeyendo(true);
    try {
      const r = await leerCaptura(file, presupuestos.map(p => p.nombre));
      if (!r.es_pago) {
        setLectura({ ok: false, texto: 'La IA no encontró un pago en esta imagen. La captura igual queda adjunta; completa los datos a mano.' });
        return;
      }
      const tipo = form.tipo; // se respeta lo que eligió (gasto o entrada); si la IA ve otra cosa, se avisa
      const existente = buscarYaRegistrado(r.tipo, r.monto, r.fecha || form.fecha);
      if (existente) setCoincide({ mov: existente, file });
      setForm(f => {
        const cambios = {
          monto: r.monto ? String(r.monto) : f.monto,
          descripcion: r.descripcion || f.descripcion,
          fecha: r.fecha || f.fecha,
          rubro: r.rubro || f.rubro,
          conFactura: r.con_factura,
        };
        if (tipo === 'egreso') {
          const pendientes = gastosF.filter(g => !gastoFijoPagado(g, parseFecha(cambios.fecha) || new Date()));
          const g = adivinarGastoFijo(r.descripcion, pendientes);
          if (g) {
            Object.assign(cambios, {
              vincular: 'gasto_fijo', gasto_fijo_id: g.id, rubro: cambios.rubro || g.rubro || '',
              ...(g.compartido_con ? { compartido: true, compartidoCon: g.compartido_con, compartidoPct: g.compartido_pct || 50 } : {}),
            });
          } else if (r.presupuesto) {
            const p = presupuestos.find(x => x.nombre === r.presupuesto);
            if (p) cambios.presupuesto_id = p.id;
          }
        }
        return { ...f, ...cambios };
      });
      setLectura({
        ok: true,
        texto: `La IA leyó la captura${r.fecha && r.fecha !== fecha ? ` (dice que el pago fue el ${formatFecha(r.fecha)})` : ''}. Revisa los datos antes de guardar.${r.tipo !== tipo ? (r.tipo === 'ingreso' ? ' Ojo: parece plata que te llegó, no un gasto.' : ' Ojo: parece un pago que hiciste, no plata que te llegó.') : ''}${r.nota ? ` Ojo: ${r.nota}` : ''}`,
      });
    } catch (e) {
      setLectura({ ok: false, texto: `${e.message} La captura igual queda adjunta como comprobante.` });
    } finally {
      setLeyendo(false);
    }
  };

  // --- Guardar ---
  const handleGuardar = async (e) => {
    e.preventDefault();
    const monto = parseFloat(form.monto);
    if (!form.bolsilloId) { setError('Elige de qué categoría sale (o entra) la plata.'); return; }
    if (!(monto > 0)) { setError('Escribe el monto.'); return; }
    const esEgreso = form.tipo === 'egreso';
    const deuda = esEgreso && form.vincular === 'deuda' ? deudas.find(d => d.id === form.deuda_id) : null;
    const gastoFijo = esEgreso && form.vincular === 'gasto_fijo' ? gastosF.find(g => g.id === form.gasto_fijo_id) : null;
    if (esEgreso && form.vincular === 'deuda' && !deuda) { setError('Elige la deuda que estás pagando.'); return; }
    if (esEgreso && form.vincular === 'gasto_fijo' && !gastoFijo) { setError('Elige el gasto fijo que estás pagando.'); return; }
    const compartido = esEgreso && form.vincular !== 'deuda' && form.compartido;
    if (compartido) {
      const err = validarCompartido({ persona: form.compartidoCon, monto, pct: form.compartidoPct });
      if (err) { setError(err); return; }
    }
    let descripcion = form.descripcion.trim() || null;
    if (deuda && !descripcion) descripcion = `Pago deuda: ${deuda.nombre}`;
    if (gastoFijo && !descripcion) descripcion = `Gasto fijo: ${gastoFijo.nombre}`;
    if (!descripcion) { setError('Escribe qué fue (ej: Supermercado, Farmacia).'); return; }

    const ok = await run(() => registrarMovimiento({
      userId,
      bolsilloId: form.bolsilloId,
      tipo: form.tipo,
      monto,
      descripcion,
      fecha: form.fecha,
      rubro: esEgreso ? (form.rubro || null) : null,
      conFactura: form.conFactura,
      presupuestoId: form.vincular === 'gasto' ? form.presupuesto_id : null,
      deuda,
      gastoFijo,
      compartido: compartido ? { persona: form.compartidoCon, pct: form.compartidoPct } : null,
      archivo: form.archivo,
    }));
    if (ok) {
      guardarBolsillo(form.bolsilloId);
      setFecha(form.fecha); // si la captura era de otro día, mostrar ese día
      cerrarForm();
    }
  };

  const handleEliminar = async (mov) => {
    let v;
    try { v = await buscarVinculos(mov.id); } catch (e) { setError(e.message); return; }
    const avisos = [];
    if (v.pagos.length) avisos.push('Es un pago de deuda: también se eliminará de Mis Deudas.');
    if (v.cobros.length) avisos.push('Es un cobro de préstamo: también se eliminará de Me Deben.');
    if (v.abonos.length) avisos.push('Es un abono a un compromiso: el abono también se eliminará.');
    if (v.compras.length) avisos.push('Está vinculado a una compra: la compra se mantiene en Mis Compras.');
    if (v.ventas.length) avisos.push('Está vinculado a una venta: la venta se mantiene en Mi Negocio.');
    if (mov.gasto_fijo_id) avisos.push('Es el pago de un gasto fijo: volverá a quedar pendiente.');
    if (!window.confirm([`¿Eliminar "${mov.descripcion || 'este movimiento'}" por ${formatCLP(mov.monto)}?`, ...avisos].join('\n\n'))) return;
    await run(() => eliminarMovimiento(mov));
  };

  // --- Piezas de la pantalla ---
  const Fila = ({ mov }) => {
    const comp = comprobanteDe(mov.id);
    const pres = presupuestos.find(p => p.id === mov.presupuesto_id);
    const gf = gastosF.find(g => g.id === mov.gasto_fijo_id);
    const egreso = mov.tipo === 'egreso';
    const detalles = [
      nombreBolsillo(mov.bolsillo_id),
      { traspaso: '↔ traspaso (no es gasto)', ajuste: '⚖️ ajuste (no es gasto)', prestamo: '🤝 préstamo' }[mov.clase] || null,
      egreso && mov.rubro ? nombreRubro(mov.rubro) : null,
      gf ? `🏠 ${gf.nombre}` : null,
      pres ? `${pres.icono || '🧺'} ${pres.nombre}` : null,
      mov.con_factura ? '🧾 con factura' : null,
    ].filter(Boolean);
    return (
      <div className="card panel" style={{ marginBottom: 8, padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: egreso ? 'var(--danger)' : 'var(--success)', fontWeight: 700 }}>{egreso ? '−' : '+'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {mov.descripcion || (egreso ? 'Salida' : 'Entrada')}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{detalles.join(' · ')}</div>
          </div>
          <strong className={egreso ? 'text-danger' : 'text-success'} style={{ whiteSpace: 'nowrap' }}>{formatCLP(mov.monto)}</strong>
          {comp && (
            <button type="button" className="btn-icon-del small" title="Ver comprobante"
              onClick={() => abrirComprobante(comp).catch(e => setError(e.message))}>📎</button>
          )}
          <button type="button" className="btn-icon-del small" title="Eliminar" onClick={() => handleEliminar(mov)}>×</button>
        </div>
      </div>
    );
  };

  const BotonAgregar = ({ tipo, texto }) => (
    <button type="button" className="btn-secondary" style={{ width: '100%', padding: '12px', fontWeight: 600 }}
      onClick={() => abrirForm(tipo)} disabled={!!form}>
      {texto}
    </button>
  );

  const renderForm = () => {
    const esEgreso = form.tipo === 'egreso';
    const pres = presupuestos.find(x => x.id === form.presupuesto_id);
    return (
      <form className="card panel" onSubmit={handleGuardar}
        style={{ marginBottom: 12, borderLeft: `4px solid ${esEgreso ? 'var(--danger)' : 'var(--success)'}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>{esEgreso ? 'Nuevo gasto' : 'Nueva entrada de plata'}</h4>
          <button type="button" className="modal-close" onClick={cerrarForm}>×</button>
        </div>

        {/* Captura primero: es lo más rápido */}
        <div className="form-group">
          <label className="btn-primary" style={{ display: 'block', textAlign: 'center', cursor: leyendo ? 'wait' : 'pointer', opacity: leyendo ? 0.7 : 1 }}>
            {leyendo ? '⏳ Leyendo la captura...' : form.archivo ? '📷 Cambiar captura' : '📷 Subir captura o foto del pago'}
            <input type="file" accept={ACEPTA_ARCHIVOS} style={{ display: 'none' }} disabled={leyendo}
              onChange={e => { handleCaptura(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          <span className="field-hint">
            {form.archivo
              ? `📎 ${form.archivo.name} — queda guardada como comprobante.`
              : 'La IA lee el monto, la fecha y qué fue. Tú revisas y guardas. También puedes llenarlo a mano.'}
          </span>
          {lectura && (
            <div style={{
              marginTop: 8, padding: '10px 12px', borderRadius: 8, fontSize: '0.85rem',
              background: lectura.ok ? 'var(--success-bg)' : 'var(--warning-bg)',
              border: `1px solid ${lectura.ok ? 'var(--success)' : 'var(--warning)'}`,
            }}>
              {lectura.ok ? '✨ ' : '⚠️ '}{lectura.texto}
            </div>
          )}
          {coincide && (
            <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, fontSize: '0.85rem', background: 'var(--warning-bg)', border: '1px solid var(--warning)' }}>
              <strong>Esto ya está registrado:</strong> «{coincide.mov.descripcion || 'movimiento'}» por {formatCLP(coincide.mov.monto)} el {formatFecha(coincide.mov.fecha)}
              {' '}en {nombreBolsillo(coincide.mov.bolsillo_id)}. Si es el mismo pago, adjunta la captura ahí y no se duplica.
              <button type="button" className="btn-primary" style={{ display: 'block', width: '100%', marginTop: 8 }}
                onClick={adjuntarAExistente} disabled={saving}>
                📎 Adjuntar la captura a ese pago
              </button>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Monto ($)</label>
          <input type="number" inputMode="numeric" value={form.monto} min="1" placeholder="0"
            onChange={e => set({ monto: e.target.value })} required />
        </div>
        <div className="form-group">
          <label>{esEgreso ? '¿Qué pagaste?' : '¿De qué es esta plata?'}</label>
          <input type="text" value={form.descripcion} onChange={e => set({ descripcion: e.target.value })}
            placeholder={esEgreso ? 'ej: Supermercado, Farmacia, Uber...' : 'ej: Venta, sueldo, transferencia de...'} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>{esEgreso ? '¿De dónde salió?' : '¿A dónde entró?'}</label>
            <select value={form.bolsilloId} onChange={e => set({ bolsilloId: e.target.value })}>
              {bolsillos.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Fecha</label>
            <input type="date" value={form.fecha} onChange={e => set({ fecha: e.target.value })} />
          </div>
        </div>

        {esEgreso && (
          <>
            <div className="form-group">
              <label>¿Qué tipo de gasto es?</label>
              <div className="tipo-toggle" style={{ flexWrap: 'wrap' }}>
                <button type="button" className={`toggle-btn ${form.vincular === 'gasto' ? 'active-red' : ''}`}
                  onClick={() => set({ vincular: 'gasto', gasto_fijo_id: '', deuda_id: '' })}>🛒 Del día</button>
                {gastosF.length > 0 && (
                  <button type="button" className={`toggle-btn ${form.vincular === 'gasto_fijo' ? 'active-red' : ''}`}
                    onClick={() => set({ vincular: 'gasto_fijo', deuda_id: '', presupuesto_id: '' })}>🏠 Gasto fijo</button>
                )}
                {deudas.length > 0 && (
                  <button type="button" className={`toggle-btn ${form.vincular === 'deuda' ? 'active-red' : ''}`}
                    onClick={() => set({ vincular: 'deuda', gasto_fijo_id: '', presupuesto_id: '', compartido: false })}>💳 Deuda</button>
                )}
              </div>
              {form.vincular === 'gasto_fijo' && (pendientesGF.length > 0 ? (
                <select style={{ marginTop: 8 }} value={form.gasto_fijo_id}
                  onChange={e => {
                    const g = gastosF.find(x => x.id === e.target.value);
                    set({
                      gasto_fijo_id: e.target.value,
                      monto: form.monto || (g ? String(g.monto) : ''),
                      rubro: form.rubro || g?.rubro || '',
                      ...(g?.compartido_con ? { compartido: true, compartidoCon: g.compartido_con, compartidoPct: g.compartido_pct || 50 } : {}),
                    });
                  }}>
                  <option value="">Elige el gasto fijo...</option>
                  {pendientesGF.map(g => <option key={g.id} value={g.id}>🏠 {g.nombre} — {formatCLP(g.monto)}</option>)}
                </select>
              ) : (
                <p className="field-hint" style={{ marginTop: 8 }}>Todos los gastos fijos ya están pagados en ese período.</p>
              ))}
              {form.vincular === 'gasto_fijo' && form.gasto_fijo_id && (
                <span className="field-hint">✅ Queda marcado como pagado en Gastos Fijos</span>
              )}
              {form.vincular === 'deuda' && (
                <select style={{ marginTop: 8 }} value={form.deuda_id} onChange={e => set({ deuda_id: e.target.value })}>
                  <option value="">Elige la deuda...</option>
                  {deudas.map(d => <option key={d.id} value={d.id}>{d.icono || '💳'} {d.nombre}{d.acreedor ? ` (${d.acreedor})` : ''}</option>)}
                </select>
              )}
            </div>

            <div className="form-group">
              <label>Rubro</label>
              <SelectRubro value={form.rubro} onChange={v => set({ rubro: v })} />
            </div>

            {form.vincular === 'gasto' && presupuestos.length > 0 && (
              <div className="form-group">
                <label>¿De qué presupuesto sale?</label>
                <select value={form.presupuesto_id} onChange={e => set({ presupuesto_id: e.target.value })}>
                  <option value="">Sin presupuesto</option>
                  {presupuestos.map(p => <option key={p.id} value={p.id}>{p.icono || '🧺'} {p.nombre}</option>)}
                </select>
                {pres && (() => {
                  const est = estadoPresupuesto(pres, gastadoPorPresupuesto(movimientos, claveMes(form.fecha))[pres.id] || 0);
                  const esteGasto = parseFloat(form.monto) || 0;
                  const quedaria = est.restante - esteGasto;
                  return (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ background: 'var(--border)', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, est.pct)}%`, height: '100%', background: est.color, borderRadius: 99 }} />
                      </div>
                      <span className="field-hint">
                        Llevas {formatCLP(est.usado)} de {formatCLP(est.tope)}
                        {esteGasto > 0 && (quedaria >= 0
                          ? ` · con este gasto te quedan ${formatCLP(quedaria)}`
                          : ` · con este gasto te pasas por ${formatCLP(Math.abs(quedaria))}`)}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}

            {form.vincular !== 'deuda' && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                  <input type="checkbox" checked={form.compartido} onChange={e => set({ compartido: e.target.checked })}
                    style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                  👥 Gasto compartido (alguien me debe una parte)
                </label>
                {form.compartido && (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <input type="text" list="personas-registro" style={{ flex: 2 }} value={form.compartidoCon}
                        onChange={e => set({ compartidoCon: e.target.value })} placeholder="¿Con quién?" />
                      <datalist id="personas-registro">{personas.map(p => <option key={p} value={p} />)}</datalist>
                      <select style={{ flex: 1 }} value={form.compartidoPct} onChange={e => set({ compartidoPct: Number(e.target.value) })}>
                        {PORCENTAJES_COMPARTIDO.map(p => <option key={p} value={p}>{p}%</option>)}
                      </select>
                    </div>
                    <span className="field-hint">
                      {form.compartidoCon.trim() || 'Esa persona'} te queda debiendo
                      {' '}{formatCLP(parteCompartida(form.monto, form.compartidoPct))} en Me Deben.
                    </span>
                  </>
                )}
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                <input type="checkbox" checked={form.conFactura} onChange={e => set({ conFactura: e.target.checked })}
                  style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                Me dieron factura
              </label>
              {form.conFactura && Number(form.monto) > 0 && (
                <span className="field-hint">IVA de este gasto: {formatCLP(ivaDesdeTotal(Number(form.monto)))} — suma a tu IVA crédito</span>
              )}
            </div>
          </>
        )}

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={cerrarForm}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={saving || leyendo}>{saving ? 'Guardando...' : 'Guardar'}</button>
        </div>
      </form>
    );
  };

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Registro Diario</h2>
          <p className="page-subtitle">Lo que pagas y lo que te entra, día a día. Sube la captura y se llena solo.</p>
        </div>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn-secondary" onClick={() => setFecha(sumarDias(fecha, -1))}>◀</button>
        <input type="date" value={fecha} onChange={e => e.target.value && setFecha(e.target.value)} style={{ width: 'auto' }} />
        <button className="btn-secondary" onClick={() => setFecha(sumarDias(fecha, 1))}>▶</button>
        {fecha !== hoy && <button className="btn-secondary" onClick={() => setFecha(hoy)}>Hoy</button>}
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{nombreDia(fecha)}</span>
      </div>

      {bolsillos.length === 0 ? (
        <div className="card panel">
          <p className="empty-text">Primero crea al menos una categoría (Efectivo, Banco...) en Mis Categorías: es de donde sale y a donde entra la plata.</p>
        </div>
      ) : (
        <>
          <div className="card panel" style={{ marginBottom: 20, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Pagaste</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800 }} className="text-danger">{formatCLP(total(salidas))}</div>
              {totalEmpresa > 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatCLP(totalEmpresa)} son del negocio</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Te entró</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800 }} className="text-success">{formatCLP(total(entradas))}</div>
            </div>
          </div>

          <Bloque titulo="Lo que pagaste hoy"
            texto="Todo lo que salió de tu plata este día: el súper, la farmacia, la cuenta de luz, la compra para la tienda. Sube la captura de la transferencia o la foto de la boleta y la IA lo completa.">
            {salidas.length === 0 && !(form?.tipo === 'egreso') && (
              <p className="empty-text" style={{ marginBottom: 10 }}>Nada registrado todavía este día.</p>
            )}
            {salidas.map(m => <Fila key={m.id} mov={m} />)}
            {form?.tipo === 'egreso' ? renderForm() : <BotonAgregar tipo="egreso" texto="+ Agregar gasto" />}
          </Bloque>

          <Bloque titulo="Lo que te entró hoy"
            texto="Plata que recibiste: ventas, transferencias, sueldo, lo que te devolvieron. Con esto Mi Día calcula cuánto puedes gastar al día.">
            {entradas.map(m => <Fila key={m.id} mov={m} />)}
            {form?.tipo === 'ingreso' ? renderForm() : <BotonAgregar tipo="ingreso" texto="+ Agregar entrada" />}
          </Bloque>

          <p className="field-hint">
            Todo lo que registras acá también aparece en Mis Categorías, Mi Día, Presupuestos y Gastos Fijos.
            Para editar un movimiento, búscalo en Mis Categorías.
          </p>
        </>
      )}
    </div>
  );
}

export default RegistroDiario;
